import { BOSToasterRef } from 'Components/Generic/BOSToaster';
import {
  getHasBeenDeletedToasterProps,
  showSuccessToast,
} from 'Components/Generic/BOSToasterUtil';
import BOSAlert from 'Components/Generic/Misc/BOSAlert';
import BOSDeleteAlert from 'Components/Generic/Misc/BOSDeleteAlert';
import PropTypes from 'prop-types';
import { createContext, useContext, useState } from 'react';

const AlertContext = createContext();
AlertContext.displayName = 'AlertContext';

export const AlertStateProvider = ({ children }) => {
  // Confirmation dialog properties
  const [confirmationDialogIcon, setConfirmationDialogIcon] = useState();
  const [confirmationDialogIntent, setConfirmationDialogIntent] =
    useState('primary');
  const [confirmationDialogTitle, setConfirmationDialogTitle] = useState();
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState();
  const [confirmAction, setConfirmAction] = useState();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Delete dialog properties
  const [deleteObjectType, setDeleteObjectType] = useState('Object');
  const [deleteDialogTitle, setDeleteDialogTitle] = useState('Delete Object?');
  const [deleteDialogMessage, setDeleteDialogMessage] = useState();
  const [deleteAction, setDeleteAction] = useState();
  const [deleteConfirmText, setDeleteConfirmText] = useState('Delete Object');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Confirm
  const setConfirmFunction = (newFunc) => {
    setConfirmAction(() => newFunc);
  };

  const openConfirmationDialog = ({
    onConfirm,
    dialogTitle,
    dialogMessage,
    icon,
    intent,
  }) => {
    onConfirm && setConfirmFunction(onConfirm);
    intent && setConfirmationDialogIntent(intent);
    icon && setConfirmationDialogIcon(icon);
    dialogTitle && setConfirmationDialogTitle(dialogTitle);
    dialogMessage && setConfirmationDialogMessage(dialogMessage);
    setConfirmDialogOpen(true);
  };

  const doAction = () => {
    confirmAction && confirmAction();
    closeDialog();
  };

  const closeDialog = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(undefined);
    setConfirmationDialogIntent('primary');
    setConfirmationDialogTitle(undefined);
    setConfirmationDialogMessage(undefined);
  };

  const showConfirmationToast = (message) => {
    showSuccessToast(message);
  };

  // Delete
  const setDeleteFunction = (newFunc) => {
    setDeleteAction(() => newFunc);
  };
  const openDeleteDialog = ({
    objectType,
    onConfirmDelete,
    dialogTitle,
    dialogMessage,
    confirmText,
  }) => {
    objectType && setDeleteObjectType(objectType);
    onConfirmDelete && setDeleteFunction(onConfirmDelete);
    let defaultDialogText = `Delete ${objectType || deleteObjectType}?`;
    setDeleteDialogTitle(dialogTitle ? dialogTitle : defaultDialogText);
    dialogMessage && setDeleteDialogMessage(dialogMessage);
    setDeleteConfirmText(
      confirmText || `Delete ${objectType || deleteObjectType}`,
    );
    setDeleteDialogOpen(true);
  };

  const doDelete = () => {
    deleteAction && deleteAction();
    closeDelDialog();
  };

  const closeDelDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteObjectType('Object');
    setDeleteAction(undefined);
    setDeleteDialogTitle(`Delete Object?`);
    setDeleteConfirmText(`Delete Object`);
    setDeleteDialogMessage(undefined);
  };

  const showDeletedToast = (objectType, confirmDeleteTextOverride) => {
    let confirmDeleteText =
      confirmDeleteTextOverride || `${objectType || 'Object'} deleted`;
    BOSToasterRef.show(getHasBeenDeletedToasterProps(confirmDeleteText));
  };

  return (
    <AlertContext.Provider
      value={{
        openConfirmationDialog,
        showConfirmationToast,
        openDeleteDialog,
        showDeletedToast,
      }}>
      <BOSAlert
        icon={confirmationDialogIcon || 'confirm'}
        intent={confirmationDialogIntent}
        isOpen={confirmDialogOpen}
        closeFunc={closeDialog}
        onConfirm={doAction}
        contentTitle={confirmationDialogTitle}
        content={confirmationDialogMessage}
      />
      <BOSDeleteAlert
        confirmText={deleteConfirmText}
        cancelText={`Cancel`}
        isOpen={deleteDialogOpen}
        closeFunc={closeDelDialog}
        onConfirm={doDelete}
        contentTitle={deleteDialogTitle}
        content={deleteDialogMessage}
      />
      {children}
    </AlertContext.Provider>
  );
};

AlertStateProvider.propTypes = {
  children: PropTypes.node,
};

export const useAlertContext = () => useContext(AlertContext);
